import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowClockwise, MagnifyingGlass, FunnelSimple,
  CaretUp, CaretDown, X,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  created: "bg-[#F9E4B7] text-[#7A5A11]",
  captured: "bg-[#D4E0D1] text-[#1A3317]",
  paid: "bg-[#D4E0D1] text-[#1A3317]",
  refunded: "bg-[#F4C7B9] text-[#7A2E18]",
  failed: "bg-[#F4C7B9] text-[#7A2E18]",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const p = new URLSearchParams();
      if (status) p.set("status", status);
      if (type) p.set("type", type);
      if (q) p.set("q", q);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      if (sort) p.set("sort", sort);
      const r = await api.get(`/finance/transactions?${p}`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, type, dateFrom, dateTo, sort]);
  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const refund = async (txnId, amount) => {
    const amt = window.prompt(`Refund amount (max ₹${amount}):`, amount);
    if (!amt) return;
    try {
      await api.post(`/finance/transactions/${txnId}/refund`, { amount: Number(amt) });
      toast.success("Refunded");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearFilters = () => {
    setStatus(""); setType(""); setQ(""); setDateFrom(""); setDateTo(""); setSort("-created_at");
  };

  // Type dropdown values — populated dynamically from the loaded rows plus
  // well-known defaults so the dropdown works even on an empty page.
  const typeOptions = useMemo(() => {
    const s = new Set(["payment", "refund", "wallet_debit", "wallet_credit"]);
    items.forEach((r) => r.type && s.add(r.type));
    return Array.from(s);
  }, [items]);

  const toggleSort = (key) => {
    if (sort === `-${key}`) setSort(key);
    else setSort(`-${key}`);
  };
  const sortIcon = (key) => {
    if (sort === key) return <CaretUp size={11} weight="bold" className="inline ml-1" />;
    if (sort === `-${key}`) return <CaretDown size={11} weight="bold" className="inline ml-1" />;
    return null;
  };

  return (
    <AppShell title="Transactions" subtitle="Module 12 · Finance">
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex flex-wrap items-center gap-3">
          <div className="relative">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8780]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer / phone / invoice / txn"
              data-testid="txn-search"
              className="h-9 pl-8 pr-3 w-72 border border-[#E5E2D9] rounded-md text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#DC2626]/20"
            />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="txn-filter-type"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="">All types</option>
            {typeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="txn-filter-status"
                  className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="">All statuses</option>
            {Object.keys(STATUS_STYLE).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-1 border border-[#E5E2D9] rounded-md pl-2 pr-1 h-9">
            <FunnelSimple size={12} weight="bold" className="text-[#8A8780]" />
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   data-testid="txn-filter-date-from"
                   className="h-8 text-xs bg-transparent focus:outline-none" />
            <span className="text-[#8A8780] text-xs">→</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   data-testid="txn-filter-date-to"
                   className="h-8 text-xs bg-transparent focus:outline-none" />
          </div>
          {(status || type || q || dateFrom || dateTo) && (
            <button onClick={clearFilters} data-testid="txn-clear-filters"
                    className="h-9 px-3 border border-[#E5E2D9] rounded-md text-xs bg-white text-[#5C5A55] inline-flex items-center gap-1 hover:bg-[#FAFAF7]">
              <X size={11} weight="bold" /> Clear
            </button>
          )}
          <div className="flex-1"/>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]" data-testid="txn-record-count">
            {loading ? "Loading…" : `${items.length} records`}
          </div>
        </div>
        <table className="w-full text-sm" data-testid="txns-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Txn No</th>
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3">Invoice</th>
              <th className="px-6 py-3">Type</th>
              <th className="px-6 py-3">Provider</th>
              <th className="px-6 py-3 text-right cursor-pointer select-none"
                  onClick={() => toggleSort("amount")} data-testid="txn-sort-amount">
                Amount{sortIcon("amount")}
              </th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3 cursor-pointer select-none"
                  onClick={() => toggleSort("created_at")} data-testid="txn-sort-date">
                Date{sortIcon("created_at")}
              </th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9} className="px-6 py-16 text-center text-[#8A8780]">
                {loading ? "Loading transactions…" : "No transactions match your filters."}
              </td></tr>
            )}
            {items.map((t, i) => (
              <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} data-testid={`txn-row-${t.id}`}>
                <td className="px-6 py-3 font-mono font-medium text-xs">{t.txn_no || t.id?.slice(0,10)}</td>
                <td className="px-6 py-3">
                  <div className="text-xs font-semibold text-[#1A1A1A] truncate max-w-[180px]" data-testid={`txn-cust-${t.id}`}>
                    {t.customer_name || "—"}
                  </div>
                  <div className="text-[10px] text-[#8A8780] font-mono">
                    {t.customer_phone || t.customer_code || ""}
                  </div>
                </td>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55]">{t.invoice_no || "—"}</td>
                <td className="px-6 py-3 text-xs capitalize">{(t.type || "").replace(/_/g, " ") || "—"}</td>
                <td className="px-6 py-3">
                  <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] bg-[#D4E0D1] text-[#1A3317] rounded">
                    {t.provider || t.method || "—"}
                  </span>
                  {t.demo && <span className="ml-1 text-[9px] uppercase tracking-[0.18em] text-[#C25D38]">demo</span>}
                  {t.provider === "manual" && (
                    <div className="mt-1 text-[10px] text-[#5C5A55] leading-tight space-y-0.5" data-testid={`txn-manual-${t.id}`}>
                      <div className="uppercase">{(t.method || "").replace(/_/g, " ")}{t.external_txn_no ? ` · ${t.external_txn_no}` : ""}</div>
                      {t.verified_by_name && <div>Verified: {t.verified_by_name}</div>}
                      {t.staff_in_charge_name && <div>Staff: {t.staff_in_charge_name}</div>}
                      {t.notes && <div className="italic truncate max-w-[160px]" title={t.notes}>{t.notes}</div>}
                    </div>
                  )}
                  {t.purpose === "sr_prepaid" && <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-[#2D5A27]" data-testid={`txn-prepaid-${t.id}`}>Prepaid · Pay &amp; Book</div>}
                  {t.group_sr_count > 1 && (
                    <div className="text-[10px] text-[#5C5A55]" data-testid={`txn-split-${t.id}`}>
                      Share {t.group_index}/{t.group_sr_count} of one ₹{Number(t.group_payment_total || 0).toFixed(2)} payment
                    </div>
                  )}
                </td>
                <td className="px-6 py-3 font-mono text-right">₹{Number(t.amount || 0).toFixed(2)}</td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATUS_STYLE[t.status] || "bg-[#E5E2D9] text-[#5C5A55]"}`}>
                    {t.status}
                  </span>
                </td>
                <td className="px-6 py-3 text-xs">
                  <div className="font-mono text-[#1A1A1A]" data-testid={`txn-date-${t.id}`}>
                    {fmtDate(t.created_at || t.date)}
                  </div>
                  <div className="text-[10px] text-[#8A8780]">{fmtTime(t.created_at || t.date)}</div>
                </td>
                <td className="px-6 py-3 text-right">
                  {(t.status === "paid" || t.status === "captured") && (
                    <button onClick={() => refund(t.id, t.amount)} data-testid={`refund-${t.id}`}
                            className="text-[#C25D38] text-xs inline-flex items-center gap-1">
                      <ArrowClockwise size={12}/>Refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
